import React, { useState, useEffect } from "react";
import { Dialog, DialogActions, DialogContent, Button, Typography } from "@mui/material";
import { useSpring, animated } from "react-spring";
import { Tooltip } from "react-tooltip"; // Updated import
import { getFirestore, collection, onSnapshot } from "firebase/firestore";
import "./MilestoneCircle.css"; // Custom styles
import MilestoneModal from './MilestoneModal';

const MilestoneCircle = () => {
  const [activeSegment, setActiveSegment] = useState(null);
  const [connectedApps, setConnectedApps] = useState([]);
  const [score, setScore] = useState(0);
  const [tooltip, setTooltip] = useState({ visible: false, label: "", x: 0, y: 0 });
  
  const db = getFirestore();

  const segments = [
    { id: 1, label: "Identity Provider (IDP)", gradientId: "gradient1", category: "idp" },
    { id: 2, label: "Communication Platform", gradientId: "gradient2", category: "communication" },
    { id: 3, label: "ITSM", gradientId: "gradient3", category: "itsm" },
    { id: 4, label: "HR Platform", gradientId: "gradient4", category: "hris" },
  ];

  const radius = 180;
  const innerRadius = radius - 70;
  const center = 250;

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "applications"), (snapshot) => {
      const apps = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setConnectedApps(apps);
      const connectedCount = apps.filter((app) => app.connected).length;
      const calculatedScore = Math.round((connectedCount / segments.length) * 100);
      setScore(calculatedScore);
    });
    return () => unsubscribe();
  }, [db]);

  const handleClick = (segment) => {
    setActiveSegment(segment); // Open modal with segment details
  };

  const handleMouseOver = (event, segment) => {
    setTooltip({
      visible: true,
      label: segment.label,
      x: event.clientX,
      y: event.clientY - 20,
    });
  };

  const handleMouseOut = () => {
    setTooltip({ visible: false, label: "", x: 0, y: 0 });
  };

  const createArcPath = (startAngle, endAngle, outerRadius, innerRadius) => {
    const angleOffset = -Math.PI / 2; // Start from top
    const start = {
      x: center + outerRadius * Math.cos(startAngle + angleOffset),
      y: center + outerRadius * Math.sin(startAngle + angleOffset)
    };
    const end = {
      x: center + outerRadius * Math.cos(endAngle + angleOffset),
      y: center + outerRadius * Math.sin(endAngle + angleOffset)
    };
    const innerStart = {
      x: center + innerRadius * Math.cos(startAngle + angleOffset),
      y: center + innerRadius * Math.sin(startAngle + angleOffset)
    };
    const innerEnd = {
      x: center + innerRadius * Math.cos(endAngle + angleOffset),
      y: center + innerRadius * Math.sin(endAngle + angleOffset)
    };

    const largeArcFlag = endAngle - startAngle <= Math.PI ? 0 : 1;

    return `
      M ${start.x} ${start.y}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}
      L ${innerEnd.x} ${innerEnd.y}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}
      Z
    `;
  };

  const isConnected = (category) => {
    return connectedApps.some((app) => app.category === category && app.connected);
  };

  const renderGradients = () => (
    <defs>
      {segments.map((segment) => (
        <linearGradient
          key={segment.gradientId}
          id={segment.gradientId}
          gradientTransform="rotate(45)"
        >
          <stop offset="0%" stopColor="#42A5F5" />
          <stop offset="100%" stopColor="#1E88E5" />
        </linearGradient>
      ))}
    </defs>
  );

  const handleSegmentClick = (segment) => {
    setActiveSegment(segment);
  };

  return (
    <div className="milestone-circle-container">
      <svg width="500" height="500" className="milestone-circle">
        {renderGradients()}
        
        {/* Render segments */}
        {segments.map((segment, index) => {
          const segmentAngle = (2 * Math.PI) / segments.length;
          const startAngle = index * segmentAngle;
          const endAngle = (index + 1) * segmentAngle;
          
          return (
            <g
              key={segment.id}
              className={`arc-segment ${isConnected(segment.category) ? "connected" : ""}`}
              onClick={() => handleSegmentClick(segment)}
              onMouseMove={(e) => handleMouseOver(e, segment)}
              onMouseOut={handleMouseOut}
            >
              <path
                d={createArcPath(startAngle, endAngle, radius, innerRadius)}
                fill={`url(#${segment.gradientId})`}
                stroke="white"
                strokeWidth="2"
              />
            </g>
          );
        })}

        {/* Center score */}
        <text 
          x={center} 
          y={center} 
          textAnchor="middle" 
          dominantBaseline="middle" 
          className="score-text"
        >
          {score}%
        </text>
      </svg>

      {/* Modal */}
      {activeSegment && (
        <MilestoneModal
          segment={activeSegment}
          connectedApps={connectedApps}
          onClose={() => setActiveSegment(null)}
        />
      )}
    </div>
  );
};

export default MilestoneCircle;